export const FileDataTypes = {
  STRING: 'string',
  BLOB: 'blob',
  ARRAY_BUFFER: 'arraybuffer',
  OBJECT: 'object',
} as const
export const emailType = 'email'
export const phoneType = 'phone'
export const booleanType = 'boolean'
export const MimeTypes = {
  AUDIO_WEBM: 'audio/webm',
  OCTET_STREAM: 'application/octet-stream',
} as const
export const ResourceTypes = {
  ENCOUNTER: 'Encounter',
} as const

export const WhatsappText = 'whatsapp'

export const TelecomUse = {
  MOBILE: 'mobile',
} as const

export const TransformConstants = {
  PATIENT: 'Patient',
  PATIENT_IDENTIFIER: 'patient-identifier',
  THOP_IDENTIFIER: 'thop-identifier',
  PHONE: 'phone',
  REGISTRATION_TYPE: 'registrationType',
} as const

export const Timeouts = {
  MOUNT_DELAY: 0,
  SCROLL_DELAY: 100,
} as const

export const ElementIds = {
  CAMP_INLINE_CONTAINER: 'camp-inline-container',
} as const

export const Intents = {
  ALL_APPOINTMENTS: 'allAppointments',
  GET_ENCOUNTER_FOR_APPOINTMENTS: 'getEncounterForAppointments',
  CLOSE_ENCOUNTER: 'closeEncounter',
  GET_ALL_VISIT_MEDICATION: 'getallVisitmedication',
  GET_PATIENT_API: 'getPatientApi',
  GET_ALL_SUB_ENCOUNTERS: 'getAllSubEncounters',
  FETCH_PROVENANCE: 'fetchProvenance',
} as const

export const Defaults = {
  PATIENT_IDENTIFIER: 'https://healthid.ndhm.gov.in',
} as const

export const PAIN_SCALE_MAPPING = {
  nh: 'No Hurt',
  hlb: 'Hurts Little Bit',
  hlm: 'Hurts Little More',
  hem: 'Hurts Even More',
  hwl: 'Hurts Whole Lot',
  hw: 'Hurts Worst',
} as const

export const ErrorMessages = {
  PATIENTS: {
    NOT_FOUND: 'No patients found with this phone number',
    SEARCH_ERROR: 'Error searching for patients',
    INVALID_PHONE: 'Please enter a valid phone number',
  },
} as const

export const NUMBER = {
  parse: (value: string | number): number => {
    if (typeof value === 'number') return value
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  },
} as const

// Visit Status Constants
export const VISIT_STATUS = {
  PLANNED: 'planned',
  ARRIVED: 'arrived',
  TRIAGED: 'triaged',
  IN_PROGRESS: 'in-progress',
  ACTIVE: 'active',
  ON_LEAVE: 'on-leave',
  FINISHED: 'finished',
  CANCELLED: 'cancelled',
  ENTERED_IN_ERROR: 'entered-in-error',
  UNKNOWN: 'unknown',
} as const

// Status display text mapping
export const STATUS_DISPLAY_TEXT = {
  [VISIT_STATUS.PLANNED]: 'Planned',
  [VISIT_STATUS.ARRIVED]: 'Arrived',
  [VISIT_STATUS.TRIAGED]: 'Accepted',
  [VISIT_STATUS.IN_PROGRESS]: 'Active',
  [VISIT_STATUS.ACTIVE]: 'Active',
  [VISIT_STATUS.ON_LEAVE]: 'Suspended',
  [VISIT_STATUS.FINISHED]: 'Completed',
  [VISIT_STATUS.CANCELLED]: 'Abandoned',
  [VISIT_STATUS.ENTERED_IN_ERROR]: 'Error',
  [VISIT_STATUS.UNKNOWN]: 'Unknown',
} as const

export const FormOptions = {
  YES_NO: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ],
  DIET: [
    { value: 'mixed', label: 'Mixed' },
    { value: 'veg', label: 'Vegetarian' },
  ],
  SLEEP: [
    { value: 'adequate', label: 'Adequate' },
    { value: 'inadequate', label: 'Inadequate' },
  ],
  POSTURE: [
    { value: 'Sitting', label: 'Sitting' },
    { value: 'Supine', label: 'Supine' },
  ],
  BLADDER: [
    { value: 'Regular', label: 'Regular' },
    { value: 'Irregular', label: 'Irregular' },
  ],
} as const

export const phoneNumberList = [
  'mobile',
  'phoneNumber',
  'phone',
  'contactNumber',
]

export const enter = 'Enter'

export const AppRoutes = {
  VISITS: '/visits',
  VITALS: 'vitals',
  HISTORY: 'history',
  SYSTEMIC: 'systemic',
  DIAGNOSIS: 'diagnosis',
  TREATMENT_PHYSIO: 'treatmentPhysio',
  MEDICATIONS: 'medications',
  LAB_ORDERS: 'lab-orders',
  DEVICES: 'devices',
  RADIOLOGY_ORDERS: 'radiology-orders',
  CANCER_SCREENING: 'cancerScreening',
  ONCOLOGY: 'oncology',
} as const

// Validation Messages
export const ValidationMessages = {
  PULSE: {
    REQUIRED: 'Pulse is required',
    MIN: 'Pulse must be at least 30',
    MAX: 'Pulse cannot exceed 200',
    NUMBER: 'Pulse must be a number',
    WHOLE_NUMBER: 'Pulse must be a whole number',
  },
  TEMPERATURE: {
    REQUIRED: 'Temperature is required',
    MIN: 'Temperature must be at least 90°F',
    MAX: 'Temperature cannot exceed 110°F',
  },
  RESPIRATORY_RATE: {
    REQUIRED: 'Respiratory rate is required',
    MIN: 'Respiratory rate must be at least 10',
    MAX: 'Respiratory rate cannot exceed 50',
    WHOLE_NUMBER: 'Respiratory rate must be a whole number',
  },
  SPO2: {
    REQUIRED: 'SpO2 is required',
    MIN: 'SpO2 must be at least 70%',
    MAX: 'SpO2 cannot exceed 100%',
  },
  SYSTOLIC: {
    REQUIRED: 'Systolic BP is required',
    MIN: 'Systolic BP must be at least 70',
    MAX: 'Systolic BP cannot exceed 250',
  },
  DIASTOLIC: {
    REQUIRED: 'Diastolic BP is required',
    MIN: 'Diastolic BP must be at least 40',
    MAX: 'Diastolic BP cannot exceed 150',
  },
  POSTURE: {
    REQUIRED: 'Posture is required',
  },
  // History Section
  WBPS: {
    REQUIRED: 'WBPS is required',
  },
  CHIEF_COMPLAINTS: {
    REQUIRED: 'Chief complaints are required',
  },
  HISTORY_OF_PRESENT_ILLNESS: {
    REQUIRED: 'History of present illness is required',
  },
  DIET: {
    REQUIRED: 'Diet is required',
  },
  SLEEP: {
    REQUIRED: 'Sleep pattern is required',
  },
  BOWEL_BLADDER: {
    REQUIRED: 'Bowel/Bladder pattern is required',
  },
  SMOKING_HISTORY: {
    REQUIRED: 'Smoking history is required',
  },
  ALCOHOL_HISTORY: {
    REQUIRED: 'Alcohol history is required',
  },
  DRUG_ALLERGIES: {
    REQUIRED: 'Drug allergy information is required',
  },
  PREVIOUS_SURGERIES: {
    REQUIRED: 'Previous surgeries information is required',
  },
  HEIGHT: {
    MIN: 'Height cannot be negative',
  },
  WEIGHT: {
    MIN: 'Weight cannot be negative',
  },
  PALLOR: {
    REQUIRED: 'Pallor information is required',
  },
  ICTERUS: {
    REQUIRED: 'Icterus information is required',
  },
  CYANOSIS: {
    REQUIRED: 'Cyanosis information is required',
  },
  CLUBBING: {
    REQUIRED: 'Clubbing information is required',
  },
  LYMPHADENOPATHY: {
    REQUIRED: 'Lymphadenopathy information is required',
  },
  EDEMA: {
    REQUIRED: 'Edema information is required',
  },
  GENERAL: {
    REQUIRED: 'This field is required',
    INVALID: 'Invalid value',
  },
} as const

export const PrintPageStyles = {
  DEFAULT: `
    body { margin: 0 !important; padding: 0 !important; }
    * { box-sizing: border-box !important; }
    @page :left, @page :right {
      margin: 0 !important;
    }
  `,
  E_PRESCRIPTION: `
    @page:first {
      margin-top: 0mm !important;
    }
    @page {
      margin-top: 15mm !important;
    }
  `,
} as const

export const DrInfoInitialState = {
  drName: '',
  department: '',
  date: '',
  drRegistrationNo: '',
  designation: '',
  signature: '',
} as const

export const VisitStatus = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const

export const ReferralStatus = {
  YES: 'Yes',
  NO: 'No',
} as const

export const TreatmentPlanCodes = {
  TREATMENT_PLAN: 'treatmentPlan',
  HOME_ADVICE: 'homeAdvice',
  FOLLOW_UP_ADVICE: 'followUpAdvice',
  FOLLOW_UP_DATE: 'followUpDate',
  REFERRAL_REQUIRED: 'referralRequired',
  NAME_OF_DOCTOR: 'nameOfDoctor',
  DEPARTMENT: 'department',
  REASON_REFERRAL: 'reasonReferral',
} as const

export const OpdPatientData = {
  MOBILE: 'mobile',
  STRING: 'string',
  YES: 'yes',
} as const

export const LAB_ORDER_STATUS = {
  ORDERED: 'ORDERED',
  COLLECTED: 'COLLECTED',
} as const
